import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useGetTransactionQuery } from '@/api/v1';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { PIXQRCodeModalContent } from '@/components/organisms/PIXQRCodeModalContent';
import { emptyTransactionResponse } from '@/data/emptyTransactionResponse';
import { useTransactionId } from '@/hooks/useTransactionId';
import { useTranslator } from '@/hooks/useTranslator';
import { CryptoAddressModal } from '@/Screens/Dashboard/Flows/common/CryptoAddressModal';
import { isPixInLocalInfo } from '@/types/transaction.ext';
import { noop } from '@/utility/noop';

enum PaymentModalType {
  pixPayment = 'pixPayment',
  cryptoPayment = 'cryptoPayment',
  completed = 'completed',
  unset = '',
}

export const TransferPayment: React.FC = (): React.ReactElement => {
  const tr = useTranslator();
  const transactionId = useTransactionId();
  const navigate = useNavigate();

  const { data: transaction = emptyTransactionResponse, status } =
    useGetTransactionQuery(transactionId);

  const cancelFlow = React.useCallback((): void => {
    navigate('..', { replace: true });
  }, [navigate]);

  const nextStep = React.useMemo((): PaymentModalType => {
    if (transaction) {
      const { blockchainInfo, localInfo } = transaction;

      if (isPixInLocalInfo(localInfo)) {
        return PaymentModalType.pixPayment;
      } else if (blockchainInfo.fundingWallet) {
        return PaymentModalType.cryptoPayment;
      } else {
        return PaymentModalType.completed;
      }
    } else {
      return PaymentModalType.unset;
    }
  }, [transaction]);

  if (status === QueryStatus.rejected) {
    navigate('/home', { replace: true });
  }

  if (status === QueryStatus.pending || status === QueryStatus.uninitialized) {
    return (
      <ApplicationModal title="" description="" onClose={noop}>
        <Box display="flex" alignItems="center" justifyContent="center">
          <CircularProgress size={96} thickness={4} />
        </Box>
      </ApplicationModal>
    );
  } else if (status === QueryStatus.rejected) {
    return <Navigate to="/home" replace={true} />;
  } else {
    switch (nextStep) {
      case PaymentModalType.pixPayment:
        return (
          <ApplicationModal
            title={tr('SEND_FUNDS_FROM_BANK')}
            description={tr('DEPOSIT_FIAT_ACCOUNT_MODAL_DESCRIPTION')}
            onClose={cancelFlow}>
            <PIXQRCodeModalContent transaction={transaction} successPath="../completed" />
          </ApplicationModal>
        );
      case PaymentModalType.cryptoPayment:
        return <CryptoAddressModal transaction={transaction} />;
      case PaymentModalType.completed:
        return <Navigate to="../completed" />;
      default:
        console.debug(`unsupported payment modal type: ${nextStep}`);
        return <></>;
    }
  }
};
